import { styled } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/system";

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.5);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
`;

export const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: "rgb(44, 52, 123)",
  boxShadow: "none",

  borderBottom: "0.3px solid #e0e0e0",
  height: "48px",
  position: "fixed",
  zIndex: theme.zIndex.drawer + 1,
}));

export const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: "48px !important",
  paddingLeft: "8px !important",

  padding: theme.spacing(0, 2),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

export const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

export const BrandText = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  fontWeight: 600,
  fontSize: "1.1rem",
  letterSpacing: "0.5px",
}));

export const ActionButtons = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

export const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
  padding: theme.spacing(0.5),
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
}));

export const NotificationBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.1)",
    fontSize: "0.65rem",
    fontWeight: 600,
    height: "18px",
    minWidth: "18px",
    padding: "0 4px",
  },
}));

export const StyledMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: "12px",
    marginTop: theme.spacing(1),
    minWidth: 320,
    maxWidth: 400,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    background: "linear-gradient(to bottom, #ffffff, #fafafa)",
  },
}));

export const MenuHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

export const MenuTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.95rem",
  color: theme.palette.text.primary,
}));

export const NotificationList = styled(Box)(({ theme }) => ({
  maxHeight: 400,
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(0, 0, 0, 0.1)",
    borderRadius: "3px",
  },
}));

export const NotificationListItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isUnread",
})(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
  transition: "background-color 0.2s ease",
  cursor: "pointer",
  position: "relative",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.02)",
  },
  "&:last-child": {
    borderBottom: "none",
  },
}));

export const NotificationMessage = styled(Typography)(({ theme }) => ({
  fontSize: "0.9rem",
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),
  fontWeight: 500,
  lineHeight: 1.4,
  wordBreak: "break-word",
  overflow: "hidden",
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
}));

export const NotificationTime = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
}));

export const ReadMoreButton = styled(Box)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "rgb(44, 52, 123)",
  fontWeight: 500,
  cursor: "pointer",
  display: "inline-block",
  marginTop: theme.spacing(0.5),
  padding: theme.spacing(0.25, 0.5),
  borderRadius: "4px",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(44, 52, 123, 0.08)",
    color: "rgb(35, 42, 100)",
  },
}));

export const NotificationLink = styled(Typography)(({ theme }) => ({
  color: "rgb(44, 52, 123)",
  fontSize: "0.85rem",
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(0.5),
}));

export const ConnectionButton = styled(Box)(({ theme, connected }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1),
  borderRadius: "6px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  backgroundColor: connected
    ? "rgba(44, 52, 123, 0.08)"
    : "rgba(0, 0, 0, 0.04)",
  border: "1px solid",
  borderColor: connected ? "rgba(44, 52, 123, 0.2)" : "rgba(0, 0, 0, 0.08)",
  "&:hover": {
    backgroundColor: connected
      ? "rgba(44, 52, 123, 0.12)"
      : "rgba(0, 0, 0, 0.06)",
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
}));

export const ConnectionIcon = styled(Box)(({ theme, connected }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "28px",
  height: "28px",
  borderRadius: "6px",
  backgroundColor: connected
    ? "rgba(76, 175, 80, 0.15)"
    : "rgba(255, 255, 255, 0.15)",
  color: connected ? "#4CAF50" : theme.palette.common.white,
  position: "relative",
}));

export const ConnectionInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
}));

export const ConnectionStatus = styled(Typography)(({ theme, connected }) => ({
  fontSize: "0.8rem",
  fontWeight: 500,
  color: connected ? "#4CAF50" : theme.palette.common.white,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  "&::before": {
    content: '""',
    display: "block",
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: connected ? "#4CAF50" : theme.palette.common.white,
    boxShadow: connected ? "0 0 0 2px rgba(76, 175, 80, 0.2)" : "none",
  },
}));

export const ConnectionPulse = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  borderRadius: "6px",
  backgroundColor: "rgba(76, 175, 80, 0.2)",
  animation: `${pulseAnimation} 2s infinite`,
}));
